/**
 * Painel Berytus › Documentos das farmácias:
 * - Categorias pedidas (prescritos, antimicrobianos, termolábeis, controlados): aprovar ou recusar (com motivo) conferindo
 *   a licença sanitária e a AFE (atividades autorizadas); revogar uma aprovada (com motivo). Antimicrobianos e controlados
 *   só aprovam com a declaração de escrituração no SNGPC marcada pela farmácia.
 * - Fila de análise dos documentos (licença sanitária, CRT, AFE; SNGPC enviado antes fica como anexo opcional): ver o
 *   arquivo, aprovar (pode ajustar a validade) ou recusar com motivo. A habilitação sai dos aprovados e vigentes.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, dateTime } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';

interface Doc { id: string; tenantId: string; tenantName: string; kind: string; label: string; number: string | null; issuedAt: string | null; expiresAt: string | null;
  renewalProtocol: boolean; fileName: string; contentType: string; bytes: number; status: string; rejectReason: string | null; createdAt: string; createdBy: string | null;
  reviewedAt: string | null; reviewedBy: string | null }
const TABS: [string, string][] = [['pending_review', 'Em análise'], ['approved', 'Aprovados'], ['rejected', 'Recusados'], ['all', 'Todos']];
const TONE: Record<string, string> = { pending_review: 'info', approved: 'ok', rejected: 'err' };
interface Category {
  tenantId: string; tenant: string; category: string; label: string; status: string; requestedAt: string | null; requestedBy: string | null;
  reviewedAt: string | null; reviewedBy: string | null; reason: string | null;
  sngpc: { required: boolean; declaredAt: string | null; declaredBy: string | null };
  documents: { licenca: string; afe: string; licencaDoc: string | null; afeDoc: string | null } | undefined;
}
const CAT_STATUS: Record<string, [string, string]> = { pending: ['em análise', 'info'], approved: ['aprovada', 'ok'], rejected: ['recusada', 'err'], revoked: ['revogada', 'off'] };
const DOC_STATE: Record<string, string> = { valid: 'válida', renewal: 'em renovação', expired: 'vencida', pending: 'em análise', rejected: 'recusada', missing: 'não enviada' };
const br = (d: string | null) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '—');

export function AdminDocuments() {
  const [status, setStatus] = useState('pending_review');
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'documents', status], queryFn: () => api<{ items: Doc[] }>(`/admin/documents?status=${status}`), refetchInterval: 60_000 });
  const review = useMutation({
    mutationFn: (v: { d: Doc; decision: 'approve' | 'reject'; reason?: string; expiresAt?: string }) =>
      api(`/admin/documents/${v.d.tenantId}/${v.d.id}/review`, { method: 'POST', json: { decision: v.decision, reason: v.reason, expiresAt: v.expiresAt } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'documents'] }),
  });
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Documentos das farmácias</h1>
          <p>Confira o arquivo e aprove ou recuse com o motivo (o cliente vê o motivo). Documentos exigidos para medicamentos: licença sanitária, CRT e AFE.
            SNGPC não é documento: antimicrobianos e controlados pedem a declaração de escrituração no SNGPC, marcada pela farmácia.</p>
        </div>
      </div>
      <Categories />
      <h2 style={{ margin: '18px 0 8px' }}>Documentos</h2>
      <div className="seg" role="group" aria-label="Situação" style={{ marginBottom: 12 }}>
        {TABS.map(([v, l]) => <button key={v} type="button" aria-pressed={status === v} onClick={() => setStatus(v)}>{l}</button>)}
      </div>
      {review.error && <ErrorBox error={review.error} />}
      {q.error ? <ErrorBox error={q.error} /> : !q.data ? <Loading /> : !q.data.items.length ? <div className="empty"><b>Nada aqui</b>Nenhum documento nesta situação.</div> : (
        <section className="panel flush">
          <div className="grid-wrap">
            <table className="grid" style={{ width: '100%', minWidth: 900 }}>
              <thead><tr><th>Conta</th><th>Documento</th><th>Número</th><th>Validade</th><th>Enviado</th><th>Situação</th><th /></tr></thead>
              <tbody>
                {q.data.items.map((d) => (
                  <tr key={d.id} style={{ cursor: 'default' }}>
                    <td><b>{d.tenantName}</b></td>
                    <td>{d.label}{d.renewalProtocol && <div className="small muted">protocolo de renovação</div>}</td>
                    <td className="mono small">{d.number ?? '—'}</td>
                    <td>{br(d.expiresAt)}</td>
                    <td className="small">{dateTime(d.createdAt)}{d.createdBy ? <div className="muted">{d.createdBy}</div> : null}</td>
                    <td><Tag tone={TONE[d.status] ?? 'off'}>{d.status === 'pending_review' ? 'em análise' : d.status === 'approved' ? 'aprovado' : d.status === 'rejected' ? 'recusado' : d.status}</Tag>
                      {d.rejectReason && <div className="small" style={{ color: 'var(--err)' }}>{d.rejectReason}</div>}
                      {d.reviewedBy && <div className="small muted">{d.reviewedBy} · {dateTime(d.reviewedAt)}</div>}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <a className="btn sm quiet" href={`/api/v1/admin/documents/${d.tenantId}/${d.id}/file`} target="_blank" rel="noreferrer">Ver arquivo</a>
                        {d.status === 'pending_review' && <>
                          <button type="button" className="btn sm" disabled={review.isPending} onClick={() => {
                            const v = window.prompt(`Aprovar ${d.label} de ${d.tenantName}. Validade (AAAA-MM-DD; deixe como está se estiver certa):`, d.expiresAt ?? '');
                            if (v == null) return;
                            if (v && !/^\d{4}-\d{2}-\d{2}$/.test(v.trim())) { window.alert('Data no formato AAAA-MM-DD.'); return; }
                            review.mutate({ d, decision: 'approve', expiresAt: v.trim() || undefined });
                          }}>Aprovar</button>
                          <button type="button" className="btn sm danger" disabled={review.isPending} onClick={() => {
                            const r = window.prompt(`Motivo da recusa (o cliente vê):`);
                            if (r && r.trim().length >= 3) review.mutate({ d, decision: 'reject', reason: r.trim() });
                          }}>Recusar</button>
                        </>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

/** Categorias pedidas pelas farmácias: a curadoria confere a licença sanitária e a AFE (atividades autorizadas). */
function Categories() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'pharmacy-categories'], queryFn: () => api<{ declaration: string; items: Category[] }>('/admin/pharmacy-categories'), refetchInterval: 60_000 });
  const decide = useMutation({
    mutationFn: (v: { c: Category; decision: 'approve' | 'reject' | 'revoke'; reason?: string }) =>
      api(`/admin/pharmacy-categories/${v.c.tenantId}/${v.c.category}/decide`, { method: 'POST', json: { decision: v.decision, reason: v.reason } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin', 'pharmacy-categories'] }); qc.invalidateQueries({ queryKey: ['admin', 'nav-counts'] }); },
  });
  const ask = (c: Category, decision: 'reject' | 'revoke') => {
    const r = window.prompt(`${decision === 'reject' ? 'Recusar' : 'Revogar'} ${c.label} de ${c.tenant}. Motivo (a farmácia vê):`);
    if (r && r.trim().length >= 3) decide.mutate({ c, decision, reason: r.trim() });
  };
  const docLink = (c: Category, id: string | null, label: string, state: string) => (
    <div>{id ? <a href={`/api/v1/admin/documents/${c.tenantId}/${id}/file`} target="_blank" rel="noreferrer">{label}</a> : label}{' '}
      <Tag tone={state === 'valid' || state === 'renewal' ? 'ok' : 'warn'}>{DOC_STATE[state] ?? state}</Tag></div>
  );
  return (
    <section className="panel flush">
      <div className="panel-head"><h2>Categorias pedidas</h2><span className="small muted">aprovar confere a licença sanitária e a AFE (atividades autorizadas); recusa e revogação pedem motivo</span></div>
      {decide.error && <div style={{ padding: '0 16px' }}><ErrorBox error={decide.error} /></div>}
      {q.error ? <div style={{ padding: 16 }}><ErrorBox error={q.error} /></div> : !q.data ? <Loading /> : !q.data.items.length ? <div className="empty"><b>Nenhuma categoria pedida</b>As farmácias marcam as categorias em Dados da farmácia.</div> : (
        <div className="grid-wrap">
          <table className="grid" style={{ width: '100%', minWidth: 960 }}>
            <colgroup><col style={{ width: 210 }} /><col style={{ width: 190 }} /><col style={{ width: 200 }} /><col style={{ width: 190 }} /><col style={{ width: 170 }} /><col /></colgroup>
            <thead><tr><th>Conta</th><th>Categoria</th><th>Licença e AFE</th><th>Declaração SNGPC</th><th>Situação</th><th /></tr></thead>
            <tbody>
              {q.data.items.map((c) => {
                const [label, tone] = CAT_STATUS[c.status] ?? [c.status, 'off'];
                return (
                  <tr key={`${c.tenantId}-${c.category}`} style={{ cursor: 'default' }}>
                    <td style={{ whiteSpace: 'normal' }}><b>{c.tenant}</b>{c.requestedAt && <div className="small muted">pedida {dateTime(c.requestedAt)}{c.requestedBy ? ` · ${c.requestedBy}` : ''}</div>}</td>
                    <td style={{ whiteSpace: 'normal' }}>{c.label}</td>
                    <td className="small" style={{ whiteSpace: 'normal' }}>{c.documents && <>{docLink(c, c.documents.licencaDoc, 'Licença sanitária', c.documents.licenca)}{docLink(c, c.documents.afeDoc, 'AFE', c.documents.afe)}</>}</td>
                    <td className="small" style={{ whiteSpace: 'normal' }}>{!c.sngpc.required ? <span className="muted">não se aplica</span>
                      : c.sngpc.declaredAt ? <><Tag tone="ok">marcada</Tag><div className="muted">{dateTime(c.sngpc.declaredAt)}{c.sngpc.declaredBy ? ` · ${c.sngpc.declaredBy}` : ''}</div></>
                      : <Tag tone="warn">não marcada</Tag>}</td>
                    <td style={{ whiteSpace: 'normal' }}><Tag tone={tone}>{label}</Tag>
                      {c.reason && <div className="small" style={{ color: c.status === 'approved' ? undefined : 'var(--err)' }}>{c.reason}</div>}
                      {c.reviewedBy && <div className="small muted">{c.reviewedBy} · {dateTime(c.reviewedAt)}</div>}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {c.status === 'pending' && <>
                          <button type="button" className="btn sm" disabled={decide.isPending}
                            onClick={() => window.confirm(`Aprovar ${c.label} de ${c.tenant}? Confira antes, na licença sanitária e na AFE, se a atividade está autorizada.`) && decide.mutate({ c, decision: 'approve' })}>Aprovar</button>
                          <button type="button" className="btn sm danger" disabled={decide.isPending} onClick={() => ask(c, 'reject')}>Recusar</button>
                        </>}
                        {c.status === 'approved' && <button type="button" className="btn sm quiet" style={{ color: 'var(--err)' }} disabled={decide.isPending} onClick={() => ask(c, 'revoke')}>Revogar</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
